/**
 * Viewport region capture for the CoPaya screenshot flow.
 *
 * Capture is DOM rasterization (modern-screenshot), not getDisplayMedia: the
 * browser permission picker would break the drag-to-select UX, and rasterizing
 * only ever sees this document, never other windows.
 *
 * Coordinates travel through three spaces:
 *  - selection rects are viewport CSS px (from pointer events),
 *  - the captured element (document.body) has its own CSS-px origin, and
 *  - the output canvas is device px (CSS px × capture scale).
 * `cropRectToCanvasPx` is the one place the mapping happens, kept pure so the
 * math is unit-testable without a real canvas.
 */

import type { Options as ScreenshotOptions } from 'modern-screenshot';

/** A rectangle in viewport CSS pixels. */
export interface SelectionRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface CapturedImage {
  /** The cropped region, in device pixels. */
  canvas: HTMLCanvasElement;
  /** CSS px → canvas px multiplier used at capture time. */
  scale: number;
}

/**
 * Cap on the capture scale. A DPR-3 phone or 4K display would otherwise
 * produce a clipboard PNG several times larger with no legibility gain.
 */
const MAX_CAPTURE_SCALE = 2;

/**
 * Placeholder for subresources whose fetch fails during rasterization — in
 * production the CSP has no connect-src carve-outs, so cross-origin images
 * (S3 attachments, imgix) are blocked and must degrade per-image instead of
 * failing the whole capture. 1×1 transparent GIF.
 */
const FAILED_FETCH_PLACEHOLDER =
  'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

/**
 * Elements carrying this attribute are excluded from the capture. The
 * selection overlay sets it as a belt-and-braces guard on top of being
 * unmounted before rasterization.
 */
export const SCREENSHOT_EXCLUDE_ATTR = 'data-screenshot-exclude';

/**
 * Resolves after the browser has painted the current DOM state. A single
 * requestAnimationFrame fires *before* paint; the second one guarantees the
 * frame in between was actually presented — so anything unmounted before the
 * call (menu, overlay) is gone from the pixels being rasterized.
 */
export function nextPaint(): Promise<void> {
  return new Promise((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => resolve());
    });
  });
}

/**
 * Map a viewport-CSS-px selection onto the captured canvas, clamped to the
 * canvas bounds.
 *
 * @param rect    selection in viewport CSS px
 * @param origin  the captured element's viewport position (its
 *                getBoundingClientRect x/y) — subtracting it converts
 *                viewport coordinates to element-local ones, which also
 *                covers any scroll offset or margin
 * @param scale   CSS px → canvas px multiplier
 * @param bounds  source canvas size in canvas px
 * @returns the source rect in canvas px, or null when the selection is
 *          degenerate or entirely outside the canvas
 */
export function cropRectToCanvasPx(
  rect: SelectionRect,
  origin: { x: number; y: number },
  scale: number,
  bounds: { width: number; height: number },
): SelectionRect | null {
  const left = (rect.x - origin.x) * scale;
  const top = (rect.y - origin.y) * scale;
  const right = left + rect.width * scale;
  const bottom = top + rect.height * scale;

  const clampedLeft = Math.max(0, Math.min(left, bounds.width));
  const clampedTop = Math.max(0, Math.min(top, bounds.height));
  const clampedRight = Math.max(0, Math.min(right, bounds.width));
  const clampedBottom = Math.max(0, Math.min(bottom, bounds.height));

  // Round the two edges, not edge + size: rounding them independently could
  // put x + width one pixel past the canvas at fractional boundaries.
  const x = Math.round(clampedLeft);
  const y = Math.round(clampedTop);
  const width = Math.round(clampedRight) - x;
  const height = Math.round(clampedBottom) - y;
  if (width < 1 || height < 1) return null;

  return { x, y, width, height };
}

/**
 * Rasterize the page and crop it to the given viewport region.
 *
 * The library is imported lazily so it never lands in the initial bundle —
 * the flow is reached only from the CoPaya menu.
 */
export async function captureViewportRegion(rect: SelectionRect): Promise<CapturedImage> {
  const scale = Math.min(window.devicePixelRatio || 1, MAX_CAPTURE_SCALE);
  const { domToCanvas } = await import('modern-screenshot');

  const options: ScreenshotOptions = {
    scale,
    fetch: { placeholderImage: FAILED_FETCH_PLACEHOLDER },
    filter: (node) => !(node instanceof Element && node.hasAttribute(SCREENSHOT_EXCLUDE_ATTR)),
  };
  const bodyRect = document.body.getBoundingClientRect();
  const full = await domToCanvas(document.body, options);

  const crop = cropRectToCanvasPx(
    rect,
    { x: bodyRect.x, y: bodyRect.y },
    scale,
    { width: full.width, height: full.height },
  );
  if (!crop) {
    throw new Error('Selection is outside the captured area');
  }

  const canvas = document.createElement('canvas');
  canvas.width = crop.width;
  canvas.height = crop.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Canvas 2D context unavailable');
  }
  ctx.drawImage(
    full,
    crop.x,
    crop.y,
    crop.width,
    crop.height,
    0,
    0,
    crop.width,
    crop.height,
  );

  return { canvas, scale };
}
